import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft,
  Download,
  Spinner,
  PenNib
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

export function CertificateView() {
  const { certificateId } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const certificateRef = useRef(null);

  useEffect(() => {
    fetchCertificate();
  }, [certificateId]);

  const fetchCertificate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/certificates/${certificateId}`, {
        withCredentials: true
      });
      setCertificate(response.data);
    } catch (error) {
      console.error('Error fetching certificate:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="flex justify-center py-20">
          <Spinner size={48} className="animate-spin text-[#2C3E50]" />
        </div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 py-12 text-center">
          <h1 className="text-2xl font-bold text-[#1A252F]">Certificado não encontrado</h1>
          <Link to="/certificates">
            <Button className="mt-4">Voltar aos Certificados</Button>
          </Link>
        </div>
      </div>
    );
  }

  const verificationUrl = `${FRONTEND_URL}/verify/${certificate.verification_code}`;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      <div className="max-w-5xl mx-auto px-6 py-8 print:p-0" data-testid="certificate-view">
        <div className="flex items-center justify-between mb-6 print:hidden">
          <Link to="/certificates" className="inline-flex items-center gap-2 text-[#5C6B7A] hover:text-[#2C3E50]">
            <ArrowLeft size={20} />
            Voltar aos Certificados
          </Link>
          <Button 
            onClick={handleDownload}
            className="bg-[#2C3E50] text-white hover:bg-[#1A252F]"
            data-testid="download-certificate"
          >
            <Download size={18} className="mr-2" />
            Baixar PDF
          </Button>
        </div>

        {/* Certificate */}
        <div 
          ref={certificateRef}
          className="certificate-container bg-white rounded-xl p-12 print:rounded-none print:shadow-none"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1755540735826-0ae8cc8b0fe4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxjZXJ0aWZpY2F0ZSUyMGJvcmRlciUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc1MzUxNjAxfDA&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="bg-white/95 rounded-lg p-12 text-center">
            {/* Header with Logo */}
            <div className="mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_tecno-training/artifacts/iaun5cww_LOGO_TS%402x.png" 
                alt="Tecnoservice" 
                className="h-16 mx-auto mb-4 object-contain"
              />
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#27AE60] mb-2">
                Certificado de Conclusão
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold text-[#2C3E50]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                Aprenda Tecnoservice
              </h1>
            </div>

            {/* Decorative line */}
            <div className="w-32 h-1 bg-[#27AE60] mx-auto mb-8" />

            {/* Body */}
            <p className="text-[#5C6B7A] mb-4">Certificamos que</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A252F] mb-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              {certificate.user_name}
            </h2>
            <p className="text-[#5C6B7A] mb-4">concluiu com sucesso o curso</p>
            <h3 className="text-xl sm:text-2xl font-semibold text-[#2C3E50] mb-8">
              {certificate.course_title}
            </h3>

            {/* Date */}
            <p className="text-[#5C6B7A] mb-12">
              Concluído em {new Date(certificate.completed_at || certificate.issued_at).toLocaleDateString('pt-BR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>

            {/* Student Signature Line */}
            <div className="max-w-xs mx-auto mb-12">
              <div className="border-b border-[#2C3E50] mb-2" />
              <p className="text-sm font-semibold text-[#1A252F]">{certificate.user_name}</p>
              <p className="text-xs text-[#5C6B7A]">Participante</p>
            </div>

            {/* Signature, Seal and QR */}
            <div className="flex justify-between items-end mt-12 px-8">
              {/* Signature */}
              <div className="text-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tecno-training/artifacts/df1g8uut_ASS.png" 
                  alt="Assinatura" 
                  className="h-16 mx-auto mb-2 object-contain"
                />
                <div className="border-t border-[#2C3E50] pt-2">
                  <p className="text-sm font-semibold text-[#1A252F]">Flávio Pontes</p>
                  <p className="text-xs text-[#5C6B7A]">Diretor de Marketing e Vendas</p>
                </div>
              </div>

              {/* Official Seal */}
              <div className="flex flex-col items-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_d24fe1d9-9454-4179-b0d7-5add74198f43/artifacts/odbsqt9a_selo%20ts.png" 
                  alt="Selo Tecnoservice" 
                  className="w-24 h-28 object-contain mb-1"
                />
                <p className="text-xs text-[#5C6B7A]">Selo Oficial</p>
              </div>

              {/* QR Code */}
              <div className="text-center">
                <QRCodeSVG 
                  value={verificationUrl}
                  size={80}
                  level="M"
                  className="mb-2"
                />
                <p className="text-xs text-[#5C6B7A]">Escaneie para verificar</p>
              </div>
            </div>

            {/* Certificate ID */}
            <div className="mt-8 pt-6 border-t border-[#BDC3C7]">
              <p className="text-xs text-[#5C6B7A]">
                ID do Certificado: {certificate.verification_code}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .certificate-container, .certificate-container * {
            visibility: visible;
          }
          .certificate-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}

export default CertificateView;
